import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { useCustomMutation, useInvalidate, useUpdate } from '@refinedev/core'
import type {
  TPublisherCopyrightDraftReviewSong,
  TPublisherPublicationApprovalDraftReviewSong,
  TPublisherRequest,
  TPublisherSong,
} from '@modules/types'
import {
  songChannel,
  TSongEventName,
  TRequestEventName,
} from '@publisher/modules/analytics/channels'
import { useAnalytics } from '@publisher/modules/analytics/hooks'
import type { TSongUpdateData } from '../types'
import {
  createUpdatedSongsArray,
  getExistingSong,
  getExistingSongs,
  getOriginalSong,
} from './fee-quoted-price/utils'
import { approvalReducer, initialState } from './approvalReducer'
import { getSubmitError } from '../../../utils/getSubmitError'

type UsePublisherApprovalParams = {
  requestId: number
  selectedRequest: TPublisherRequest
  apiUrl: string
  onDrawerClose: () => void
  onReviewSuccess: () => void
}

const buildSongsFromOriginal = (
  selectedRequest: TPublisherRequest | undefined,
): TPublisherPublicationApprovalDraftReviewSong[] => {
  if (!selectedRequest?.songs) return []

  return selectedRequest.songs.map((song: TPublisherSong) => {
    const songData: TPublisherPublicationApprovalDraftReviewSong = {
      songId: song.id,
      isApproved: song.approvalDecision?.isApproved === 1 ? true : null,
    }

    if (song.counterOfferFees && song.counterOfferFees.length > 0) {
      const printCounterOffer = song.counterOfferFees.find(
        cof => cof.counterOfferType === 1 || cof.counterOfferType === 3,
      )
      const digitalCounterOffer = song.counterOfferFees.find(
        cof => cof.counterOfferType === 2 || cof.counterOfferType === 3,
      )

      if (printCounterOffer) {
        songData.printIsCounterOffered = true
        songData.printCounterOfferFee = printCounterOffer.proposedFee
      }
      if (digitalCounterOffer) {
        songData.digitalIsCounterOffered = true
        songData.digitalCounterOfferFee = digitalCounterOffer.proposedFee
      }
    }

    return songData
  })
}

export const usePublisherApproval = ({
  requestId,
  selectedRequest,
  apiUrl,
  onDrawerClose,
  onReviewSuccess,
}: UsePublisherApprovalParams) => {
  const [state, dispatch] = useReducer(approvalReducer, initialState)
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({})
  const { mutate: patchPublisherRequestDraft, isLoading } = useUpdate()
  const { mutate: sendReview, isPending: isSendingReview } = useCustomMutation()
  const invalidate = useInvalidate()
  const { channels } = useAnalytics()

  useEffect(() => {
    dispatch({
      type: 'SYNC_FROM_REQUEST',
      payload: { selectedRequest },
    })
  }, [selectedRequest])

  const patchDraftReviewSongs = useCallback(
    (
      songs: TPublisherCopyrightDraftReviewSong[],
      options?: { onSuccess?: () => void; onError?: () => void },
    ) => {
      patchPublisherRequestDraft(
        {
          resource: 'publisher-request',
          id: requestId,
          values: {
            draftReview: {
              type: selectedRequest.draftReview?.type || 1,
              songs,
            },
          },
          meta: {
            method: 'PATCH',
            disableNotification: true,
          },
        },
        {
          onSuccess: () => options?.onSuccess?.(),
          onError: () => options?.onError?.(),
        },
      )
    },
    [patchPublisherRequestDraft, requestId, selectedRequest.draftReview?.type],
  )

  const updateSongStatusWithData = useCallback(
    (
      songData: TSongUpdateData,
      options?: { onSuccess?: () => void; onError?: () => void; onFinally?: () => void },
    ) => {
      const existingSongs = getExistingSongs(selectedRequest) as TSongUpdateData[]
      const updatedSongs = createUpdatedSongsArray(existingSongs, songData)
      patchDraftReviewSongs(updatedSongs as TPublisherCopyrightDraftReviewSong[], {
        onSuccess: () => {
          options?.onSuccess?.()
          options?.onFinally?.()
        },
        onError: () => {
          options?.onError?.()
          options?.onFinally?.()
        },
      })
    },
    [patchDraftReviewSongs, selectedRequest],
  )

  const songsToSubmit = useMemo(() => {
    if (selectedRequest?.draftReview?.songs && selectedRequest.draftReview.songs.length > 0) {
      return selectedRequest.draftReview.songs
    }
    return buildSongsFromOriginal(selectedRequest)
  }, [selectedRequest])

  const handleSendReview = useCallback(() => {
    channels.request.send({
      event: TRequestEventName.Review,
      requestId,
    })

    sendReview(
      {
        url: `${apiUrl}/publisher-request/${requestId}/review`,
        method: 'post',
        values: {
          songs: songsToSubmit,
        },
      },
      {
        onSuccess: () => {
          onDrawerClose()
          invalidate({
            resource: 'publisher-request',
            id: requestId,
            invalidates: ['list'],
          })
          onReviewSuccess()
        },
        onError: error => {
          let serverErrors: Record<string, string[]> = {}
          if (error && typeof error === 'object' && 'data' in error && error.data) {
            const errorData = error.data as { errors?: Record<string, string[]> }
            if (errorData.errors) {
              serverErrors = errorData.errors
            }
          }

          const { validationErrors: extractedErrors } = getSubmitError(
            error.message || 'An error occurred while submitting the review',
            selectedRequest?.songs,
            serverErrors,
          )
          setValidationErrors(extractedErrors)
        },
      },
    )
  }, [
    apiUrl,
    channels,
    invalidate,
    onDrawerClose,
    onReviewSuccess,
    requestId,
    selectedRequest?.songs,
    sendReview,
    songsToSubmit,
  ])

  const getCopyrightNoticeToUse = useCallback(
    (
      currentCopyrightNotice: string | undefined,
      existingSong: { copyrightNotice?: string | null } | undefined,
      originalSong: { suggestedCopyrightNotice?: string | null } | undefined,
    ): string | null => {
      return currentCopyrightNotice !== undefined
        ? currentCopyrightNotice
        : existingSong?.copyrightNotice || originalSong?.suggestedCopyrightNotice || null
    },
    [],
  )

  const handleApproveSong = useCallback(
    (songId: number, currentCopyrightNotice?: string) => {
      songChannel.send({
        event: TSongEventName.Approve,
        requestId,
        songId,
      })

      const existingSong = getExistingSong(selectedRequest, songId)
      const originalSong = getOriginalSong(selectedRequest, songId)
      const copyrightNotice = getCopyrightNoticeToUse(
        currentCopyrightNotice,
        existingSong,
        originalSong,
      )

      dispatch({ type: 'SET_PROCESSING', payload: { songId, action: 'approve' } })
      updateSongStatusWithData(
        {
          songId,
          isApproved: true,
          copyrightNotice,
        },
        { onFinally: () => dispatch({ type: 'CLEAR_PROCESSING' }) },
      )
    },
    [getCopyrightNoticeToUse, requestId, selectedRequest, updateSongStatusWithData],
  )

  const handleRejectSong = useCallback(
    (songId: number, rejectReason: string, currentCopyrightNotice?: string) => {
      songChannel.send({
        event: TSongEventName.Reject,
        requestId,
        songId,
        reason: rejectReason,
      })

      const existingSong = getExistingSong(selectedRequest, songId)
      const originalSong = getOriginalSong(selectedRequest, songId)
      const copyrightNotice = getCopyrightNoticeToUse(
        currentCopyrightNotice,
        existingSong,
        originalSong,
      )

      dispatch({ type: 'SET_PROCESSING', payload: { songId, action: 'reject' } })
      updateSongStatusWithData(
        {
          songId,
          isApproved: false,
          copyrightNotice,
          rejectReason,
        },
        { onFinally: () => dispatch({ type: 'CLEAR_PROCESSING' }) },
      )
    },
    [getCopyrightNoticeToUse, requestId, selectedRequest, updateSongStatusWithData],
  )

  const handleChangeStatus = useCallback(
    (songId: number) => {
      const originalSong = getOriginalSong(selectedRequest, songId)
      const existingSong = getExistingSong(selectedRequest, songId)

      dispatch({ type: 'SET_PROCESSING', payload: { songId, action: 'reset' } })
      updateSongStatusWithData(
        {
          songId,
          isApproved: null,
          copyrightNotice: existingSong?.copyrightNotice || originalSong?.copyrightNotice || null,
          printCounterOfferFee: null,
          printIsCounterOffered: false,
          digitalCounterOfferFee: null,
          digitalIsCounterOffered: false,
          rejectReason: null,
        },
        { onFinally: () => dispatch({ type: 'CLEAR_PROCESSING' }) },
      )
    },
    [selectedRequest, updateSongStatusWithData],
  )

  const handleFeeQuotedPriceEdit = useCallback(
    (songId: number, feeType?: 'print' | 'digital') => {
      const key = feeType ? `${songId}_${feeType}` : `${songId}`
      dispatch({ type: 'SET_EDITING', payload: { key, value: true } })

      const existingSong = getExistingSong(selectedRequest, songId)
      let initialValue = ''

      if (feeType === 'print' && existingSong?.printCounterOfferFee != null) {
        initialValue = existingSong.printCounterOfferFee.toString()
      } else if (feeType === 'digital' && existingSong?.digitalCounterOfferFee != null) {
        initialValue = existingSong.digitalCounterOfferFee.toString()
      }

      dispatch({ type: 'SET_VALUE', payload: { key, value: initialValue } })
    },
    [selectedRequest],
  )

  const handleFeeQuotedPriceChange = useCallback(
    (songId: number, value: string, feeType?: 'print' | 'digital') => {
      const key = feeType ? `${songId}_${feeType}` : `${songId}`
      dispatch({ type: 'SET_VALUE', payload: { key, value } })
    },
    [],
  )

  const handleFeeQuotedPriceSave = useCallback(
    (songId: number, feeType: 'print' | 'digital') => {
      const key = `${songId}_${feeType}`
      const value = state.feeQuotedPriceValues[key]
      if (!value || Number.isNaN(Number(value))) {
        return
      }

      dispatch({ type: 'SET_PROCESSING', payload: { songId, action: 'reset' } })

      const existingSong = getExistingSong(selectedRequest, songId)
      const originalSong = getOriginalSong(selectedRequest, songId)

      const songData: TSongUpdateData = {
        songId,
        isApproved: false,
        rejectReason: 'Counteroffer',
        copyrightNotice: existingSong?.copyrightNotice || originalSong?.copyrightNotice || null,
      }

      songChannel.send({
        event: TSongEventName.Counteroffer,
        requestId,
        songId,
      })

      if (feeType === 'print') {
        songData.printIsCounterOffered = true
        songData.printCounterOfferFee = Number(value)
      } else {
        songData.digitalIsCounterOffered = true
        songData.digitalCounterOfferFee = Number(value)
      }

      updateSongStatusWithData(songData, {
        onSuccess: () => {
          dispatch({ type: 'CLEAR_KEYS', payload: { keys: [key] } })
        },
        onFinally: () => dispatch({ type: 'CLEAR_PROCESSING' }),
      })
    },
    [requestId, selectedRequest, state.feeQuotedPriceValues, updateSongStatusWithData],
  )

  const handleDualFeeQuotedPriceSave = useCallback(
    (songId: number) => {
      const printKey = `${songId}_print`
      const digitalKey = `${songId}_digital`
      const printValue = state.feeQuotedPriceValues[printKey]
      const digitalValue = state.feeQuotedPriceValues[digitalKey]

      if (
        (!printValue || Number.isNaN(Number(printValue))) &&
        (!digitalValue || Number.isNaN(Number(digitalValue)))
      ) {
        return
      }

      dispatch({ type: 'SET_PROCESSING', payload: { songId, action: 'reset' } })
      songChannel.send({
        event: TSongEventName.Counteroffer,
        requestId,
        songId,
      })

      const existingSong = getExistingSong(selectedRequest, songId)
      const originalSong = getOriginalSong(selectedRequest, songId)

      const songData: TSongUpdateData = {
        songId,
        isApproved: false,
        rejectReason: 'Counteroffer',
        copyrightNotice: existingSong?.copyrightNotice || originalSong?.copyrightNotice || null,
      }

      if (printValue && !Number.isNaN(Number(printValue))) {
        songData.printIsCounterOffered = true
        songData.printCounterOfferFee = Number(printValue)
      }

      if (digitalValue && !Number.isNaN(Number(digitalValue))) {
        songData.digitalIsCounterOffered = true
        songData.digitalCounterOfferFee = Number(digitalValue)
      }

      updateSongStatusWithData(songData, {
        onSuccess: () => {
          dispatch({ type: 'CLEAR_KEYS', payload: { keys: [printKey, digitalKey] } })
        },
        onFinally: () => dispatch({ type: 'CLEAR_PROCESSING' }),
      })
    },
    [requestId, selectedRequest, state.feeQuotedPriceValues, updateSongStatusWithData],
  )

  const handleFeeQuotedPriceCancel = useCallback(
    (songId: number, feeType?: 'print' | 'digital') => {
      const key = feeType ? `${songId}_${feeType}` : `${songId}`
      dispatch({ type: 'SET_EDITING', payload: { key, value: false } })

      const originalSong = getOriginalSong(selectedRequest, songId)
      let originalPrice = 0

      if (feeType === 'print') {
        originalPrice = originalSong?.printPublisherFee ?? 0
      } else if (feeType === 'digital') {
        originalPrice = originalSong?.digitalPublisherFee ?? 0
      }

      dispatch({ type: 'SET_VALUE', payload: { key, value: originalPrice.toString() } })
    },
    [selectedRequest],
  )

  return {
    songsToSubmit,
    handleApproveSong,
    handleRejectSong,
    handleChangeStatus,
    isUpdatingSongStatus: isLoading,
    processingSongId: state.processingSongId,
    processingAction: state.processingAction,
    validationErrors,
    isSendingReview,
    handleSendReview,
    feeQuotedPriceControl: {
      editingState: state.editingState,
      feeQuotedPriceValues: state.feeQuotedPriceValues,
      isQuotedPriceUpdating: isLoading,
      handleFeeQuotedPriceEdit,
      handleFeeQuotedPriceChange,
      handleFeeQuotedPriceSave,
      handleDualFeeQuotedPriceSave,
      handleFeeQuotedPriceCancel,
    },
  }
}
